'use client';
import React, { useState, useEffect, useMemo } from 'react';
import { Box, Button, CircularProgress, FormControl, FormControlLabel, InputLabel, MenuItem, Paper, Radio, RadioGroup, Select, Stack, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Tabs, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { ArrowBack as ArrowBackIcon, FileDownload as DownloadIcon, BarChart, InfoOutlined, Person, Public, School } from '@mui/icons-material';
import { Investigador, VersionSelection } from './types/rankingTypes';
import { InfoBlock } from './InfoBlock';
import { PosicionCell } from './PosicionCell';
import ConsultasService from './services/ConsultasService';
import { useNavigate } from 'react-router-dom';

export interface AreaActiva {
    iddecinvrankinginvestigadorarea: number;
    nivel: string;
    nombre: string;
    estado: string;
    top: number;
}

export const RankingVisualizador = () => {
    const navigate = useNavigate();
    const [anio, setAnio] = useState(2026);
    const [cargo, setCargo] = useState("Profesor");
    const [tabValue, setTabValue] = useState<string>('investigadores');
    const [filtroArea, setFiltroArea] = useState<string | null>(null);
    const [cuartil, setCuartil] = useState<string>("Q1");
    const [dataInvestigadores, setDataInvestigadores] = useState<Investigador[]>([]);
    const [filtroTipoInv, setFiltroTipoInv] = useState<'' | 'Profesor' | 'Técnico de investigación'>(
        (cargo as '' | 'Profesor' | 'Técnico de investigación') || ''
    );
    const [tipo, setTipo] = useState<string>('investigadores');
    const [investigadoresAPI, setInvestigadoresAPI] = useState<any[]>([]);
    const [colabIntAPI, setColabIntAPI] = useState<any[]>([]);
    const [unidadesAPI, setUnidadesAPI] = useState<any[]>([]);
    const [centrosAPI, setCentrosAPI] = useState<any[]>([]);
    const [fechaCreacion, setFechaCreacion] = useState("")
    const autocitasLabel = 'S' === 'S' ? 'Incluye Autocitas' : 'No Incluye Autocitas';
    const [filtroRanking, setFiltroRanking] = useState<string | null>(null);
    const [isOficial, setIsoficial] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [areasConfig, setAreasConfig] = useState<AreaActiva[]>([]);
    const [filtroSubArea, setFiltroSubArea] = useState<string | null>(null);
    const [subAreasDesdeAPI, setSubAreaDesdeAPI] = useState<AreaActiva[]>([]);
    const tiposRankingUnicos = ['Institucional', 'Área QS', 'Subárea QS'];
    const mostrarColumnaArea = filtroTipoInv === 'Profesor' && filtroRanking !== 'Institucional' && filtroRanking !== 'Subárea QS';
    const mostrarColumnaSubarea = filtroTipoInv === 'Profesor' && filtroRanking !== 'Institucional' && filtroRanking !== 'Área QS' && filtroRanking !== 'Subárea QS';
    const mostrarColumnaCuartil = filtroTipoInv === 'Profesor' && filtroRanking !== 'Institucional' && filtroRanking !== 'Área QS';
    const [criterioOrden, setCriterioOrden] = useState<string>('calificacion');

    const [loadingDatos, setLoadingDatos] = useState(false); // <-- NUEVO ESTADO DE CARGA
    let currentData: any[] = [];
    let columns: { id: string, label: string, numeric?: boolean }[] = [];

    const areasDesdeAPI = useMemo(() => {
        if (!areasConfig) return [];
        return areasConfig
            .filter(area => area.nivel === 'Área QS')
    }, [areasConfig]);

    useEffect(() => {
        if (tipo == "investigadores") {
            setTabValue("investigadores");
        } else if (tipo == "tecdocente") {
            setTabValue("tecdocente");
        } else if (tipo == "centros") {
            setTabValue("unidades");
        }
    }, [tipo]);

    const formatearFecha = (fechaOriginal: string) => {
        if (!fechaOriginal) return '';
        const date = new Date(fechaOriginal);
        if (isNaN(date.getTime())) return fechaOriginal;
        const aaaa = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        const hh = String(date.getHours()).padStart(2, '0');
        const min = String(date.getMinutes()).padStart(2, '0');
        const ss = String(date.getSeconds()).padStart(2, '0');
        return `${aaaa}-${mm}-${dd} ${hh}:${min}:${ss}`;
    };

    useEffect(() => {
        const fetchAreasActivas = async () => {
            try {
                const resultado = await ConsultasService.GetRankingAreasActivas();
                if (resultado && resultado.listData) {
                    setAreasConfig(resultado.listData);
                }
            } catch (error) {
                console.error("Error cargando configuración de áreas:", error);
            }
        }
        fetchAreasActivas();
    }, []);

    useEffect(() => {
        if (tabValue === "investigadores" && filtroTipoInv === 'Profesor' && !filtroRanking) {
            setFiltroRanking(tiposRankingUnicos[0]);
        }
    }, [tabValue, filtroTipoInv, filtroRanking]);

    // --- EFECTO CORREGIDO PARA ASIGNAR EL ÁREA AUTOMÁTICAMENTE ---
    useEffect(() => {
        const cargarAreaInicial = async () => {
            if (tabValue === "investigadores" && filtroTipoInv === 'Profesor') {
                if (areasDesdeAPI.length > 0) {
                    const existeArea = areasDesdeAPI.some(item => {
                        if (!filtroArea) return false;
                        try {
                            const areaParsed: AreaActiva = JSON.parse(filtroArea);
                            return areaParsed.iddecinvrankinginvestigadorarea === item.iddecinvrankinginvestigadorarea;
                        } catch {
                            return false;
                        }
                    });

                    if (!filtroArea || !existeArea) {
                        const primeraArea = areasDesdeAPI[0];
                        const areaString = JSON.stringify(primeraArea);
                        setFiltroArea(areaString);
                    }
                } else {
                    setFiltroArea('');
                }
            }
        };

        cargarAreaInicial();
        // AQUÍ ESTÁ EL CAMBIO: Se agregó 'filtroArea' al final del arreglo
    }, [filtroRanking, areasDesdeAPI, tabValue, filtroTipoInv, filtroArea]);

    // --- NUEVO EFECTO: CARGAR SUBÁREAS AUTOMÁTICAMENTE ---
    useEffect(() => {
        const cargarSubAreas = async () => {
            // Validamos que estemos en la pestaña correcta
            if (tabValue !== "investigadores" || filtroTipoInv !== 'Profesor') return;

            // CASO 1: Cuando estamos en Subárea QS caso VIEJO
            {/* 
            if (filtroRanking === 'Subárea QS' && filtroArea) {
                try {
                    const areaSeleccionada: AreaActiva = JSON.parse(filtroArea);
                    const resultado = await ConsultasService.GetRankingSubAreasActivas(areaSeleccionada.iddecinvrankinginvestigadorarea);

                    if (resultado && resultado.listData && resultado.listData.length > 0) {
                        setSubAreaDesdeAPI(resultado.listData);
                        setFiltroSubArea(JSON.stringify(resultado.listData[0]));
                    } else {
                        setSubAreaDesdeAPI([]);
                        setFiltroSubArea(null);
                    }
                } catch (error) {
                    console.error("Error cargando configuración de subareas automáticamente:", error);
                    setSubAreaDesdeAPI([]);
                    setFiltroSubArea(null);
                }
            }*/}
            // CASO 2: Cuando estamos en FWCI QS
            if (filtroRanking === 'Subárea QS' && cuartil) {
                try {
                    // Llamamos a tu servicio usando el cuartil actual y el id
                    const resultado = await ConsultasService.GetCuartilConArea(cuartil, 93);

                    if (resultado && resultado.listData && resultado.listData.length > 0) {
                        setSubAreaDesdeAPI(resultado.listData);
                        // Seteamos la primera opción automáticamente en formato JSON (como lo espera tu API)
                        setFiltroSubArea(JSON.stringify(resultado.listData[0]));
                    } else {
                        setSubAreaDesdeAPI([]);
                        setFiltroSubArea(null);
                    }
                } catch (error) {
                    console.error("Error cargando cuartiles:", error);
                    setSubAreaDesdeAPI([]);
                    setFiltroSubArea(null);
                }
            }
            // CASO 3: Limpieza (Si cambian a Institucional o Área QS)
            else if (filtroRanking !== 'Subárea QS') {
                setSubAreaDesdeAPI([]);
                setFiltroSubArea(null);
            }
        };

        cargarSubAreas();
    }, [filtroRanking, filtroArea, tabValue, filtroTipoInv, cuartil]);

    switch (tabValue) {
        case 'investigadores':
        case 'tecdocente':
            currentData = investigadoresAPI;// (CONECTADO A LA API)
            columns = [
                { id: 'posicion', label: 'Pos.', numeric: false },
                { id: 'nombre', label: 'Nombre' },
                { id: 'unidad', label: 'Unidad' },
                ...(mostrarColumnaArea ? [{ id: 'area', label: 'Área' }] : []),
                { id: 'ranking', label: 'Calificación', numeric: true },
                { id: 'publicaciones', label: 'Publicaciones', numeric: true },
                { id: 'citaciones', label: 'Citaciones', numeric: true },
                { id: 'productividad', label: 'Productividad', numeric: true },
                { id: 'calidad', label: 'Calidad', numeric: true },
            ];
            break;
        case 'colaboracion': // Colaboración (CONECTADO A LA API)
            currentData = colabIntAPI;
            columns = [
                { id: 'profesor', label: 'Investigador' },
                { id: 'scopusId', label: 'Scopus ID' },
                { id: 'produccion', label: 'Publicaciones', numeric: true },
                { id: 'prodInternacional', label: 'Colab. Int.', numeric: true },
                { id: 'porcentaje', label: '% Colab.', numeric: true },
                { id: 'totalPaises', label: 'L', numeric: true },
                { id: 'colaboradores', label: 'P', numeric: true },
                { id: 'irnAjustado', label: 'IRN (L-1)', numeric: true },
                { id: 'irnNormalizado', label: 'IRN (L-1) Norm.', numeric: true },
                { id: 'prodInternacionalNorm', label: 'Colab. Int. Norm.', numeric: true },
                { id: 'calificacion', label: 'Indicador Final', numeric: true },
            ];
            break;
        case 'unidades': // Unidades (Conectado a API)
            currentData = [...unidadesAPI].sort((a, b) => {
                const valA = Number(a[criterioOrden]) || 0;
                const valB = Number(b[criterioOrden]) || 0;
                return valB - valA;
            });
            columns = [
                { id: 'unidad', label: 'Centro' },
                { id: 'totalHora', label: 'Horas de investigación' },
                { id: 'totalPaises', label: 'L' },
                { id: 'colaboradores', label: 'P' },
                { id: 'produccion', label: 'Producción científica' },
                { id: 'productividad', label: 'Productividad científica' },
                { id: 'porcentaje', label: '% Colaboración internacional' },
                { id: 'prodInternacionalNorm', label: 'Producción Internacional Normalizado' },
                { id: 'fwci', label: 'FWCI' },
                { id: 'irnAjustado', label: 'IRN (L-1)' },
                { id: 'irnNormalizado', label: 'IRN (L.1) Normalizado' },
                { id: 'calificacion', label: 'Colaboración internacional' },
            ];
            break;
        case 'centros': // Centros (Con ordenamiento dinámico)
            currentData = [...centrosAPI].sort((a, b) => {
                const valA = Number(a[criterioOrden]) || 0;
                const valB = Number(b[criterioOrden]) || 0;
                return valB - valA;
            });
            columns = [
                { id: 'unidad', label: 'Centro' },
                { id: 'totalHora', label: 'Total Hora' },
                { id: 'totalPaises', label: 'Total Países' },
                { id: 'colaboradores', label: 'Colaboradores' },
                { id: 'produccion', label: 'Producción' },
                { id: 'productividad', label: 'Productividad' },
                { id: 'porcentaje', label: 'Porcentaje' },
                { id: 'prodInternacionalNorm', label: 'Producción Internacional Normalizado' },
                { id: 'fwci', label: 'FWCI' },
                { id: 'irnAjustado', label: 'Irn Ajustado' },
                { id: 'irnNormalizado', label: 'Irn Normalizado' },
                { id: 'calificacion', label: 'Calificación' },
            ];
            break;
    }

    const paginatedData = currentData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);


    useEffect(() => {
        const fetchDatos = async () => {
            if (tipo === "investigadores") {
                if (tabValue === "investigadores") {
                    // Bloquear consulta si el área no es válida para el ranking actual
                    if (filtroTipoInv === 'Profesor' && filtroRanking !== 'Institucional') {
                        const existeArea = areasDesdeAPI.some(item => item.nombre === filtroArea);
                        if (!filtroArea || existeArea) {
                            return;
                        }
                    }
                    setLoadingDatos(true); // Mostrar loading
                    try {
                        let response: any = [];
                        let areaParam = 'Todas';

                        // 1. PRIMERO verificamos si estamos en Subárea QS (el if más restrictivo)
                        if (filtroTipoInv === 'Profesor' && filtroRanking === 'Subárea QS' && filtroSubArea) {
                            areaParam = filtroSubArea.trim();
                            response = await ConsultasService.GetRankingOffical("P", JSON.parse(areaParam).nombre);
                        }
                        // 2. LUEGO verificamos si estamos por Área QS (o distinto a Institucional)
                        else if (filtroTipoInv === 'Profesor' && filtroRanking !== 'Institucional' && filtroArea) {
                            areaParam = filtroArea.trim();
                            response = await ConsultasService.GetRankingOffical("P", JSON.parse(areaParam).nombre);
                        }
                        // 3. Caso por defecto (Institucional)
                        else {
                            response = await ConsultasService.GetRankingOffical("P", areaParam);
                        }
                        if (response && response.data.listData) {
                            setFechaCreacion(response.data.fecha);
                            setInvestigadoresAPI(response.data.listData);
                        } else {
                            setInvestigadoresAPI([]);
                        }

                    } catch (error) {
                        console.error("Error al obtener investigadores del API:", error);
                        setInvestigadoresAPI([]);
                    } finally {
                        setLoadingDatos(false); // Ocultar loading
                    }
                } else if (tabValue === "colaboracion") {
                    try {
                        let responseINT: any = [];
                        responseINT = await ConsultasService.GetColabInternacionalOficial();
                        if (responseINT && responseINT.listData) {
                            setColabIntAPI(responseINT.listData);
                        }
                    } catch (error) {
                        console.error("Error al obtener investigadores del API:", error);
                        setInvestigadoresAPI([]);
                    } finally {
                        setLoadingDatos(false); // Ocultar loading
                    }
                }
            }
            else if (tipo === "tecdocente") {
                setLoadingDatos(true); // Mostrar loading
                try {
                    let response: any = [];

                    response = await ConsultasService.GetRankingOffical("T", 'Todas');

                    if (response && response.data.listData) {
                        setFechaCreacion(response.data.fecha);
                        setInvestigadoresAPI(response.data.listData);
                    } else {
                        setInvestigadoresAPI([]);
                    }

                } catch (error) {
                    console.error("Error al obtener Técnico Docente del API:", error);
                    setInvestigadoresAPI([]);
                } finally {
                    setLoadingDatos(false); // Ocultar loading
                }
            }
            else if (tipo === "centros") {
                if (tabValue === "unidades") {
                    try {
                        const response = await ConsultasService.GetRankingUnidadesOffical();
                        if (response && response.data.listData) {
                            setUnidadesAPI(response.data.listData);
                            setFechaCreacion(response.data.fecha);
                        }
                    } catch (error) {
                        console.error("Error al obtener GetRankingUnidadesOffical del API:", error);
                        setUnidadesAPI([]);
                    } finally {
                        setLoadingDatos(false);
                    }

                } else if (tabValue == "centros") {
                    try {
                        const response = await ConsultasService.GetRankingUnidadesOfficialByType("Centro");
                        if (response && response.data.listData) {
                            setCentrosAPI(response.data.listData);
                            setFechaCreacion(response.data.fecha);
                        }
                    } catch (error) {
                        console.error("Error al obtener GetRankingUnidadesOfficialByType del API:", error);
                        setCentrosAPI([]);
                    } finally {
                        setLoadingDatos(false);
                    }
                }
            }
        };
        fetchDatos();
    }, [filtroArea, filtroSubArea, filtroRanking, filtroTipoInv, tabValue, areasDesdeAPI]);

    const convertToCSV = (data: any[], columns: { id: string, label: string }[]): string => {
        const headers = columns.map(c => c.label);
        const csvRows = [];
        const BOM = '\uFEFF';
        csvRows.push(headers.join(';'));

        for (const item of data) {
            const row = columns.map(col => {
                const val = item[col.id];
                return `"${String(val ?? '').replace(/"/g, '""')}"`;
            });
            csvRows.push(row.join(';'));
        }
        return BOM + csvRows.join('\n');
    };

    const handleDownloadExcel = () => {
        if (currentData.length === 0) {
            alert("No hay datos para descargar.");
            return;
        }
        const csvContent = convertToCSV(currentData, columns);
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        const fileNamesMap: Record<string, string> = {
            'investigadores': 'investigadores',
            'colaboracion': 'ranking_colaboracion',
            'unidades': 'unidades_metricas',
            'centros': 'centros_metricas',
        };
        const nombreSeccion = fileNamesMap[tabValue] || 'reporte';
        const filename = `ranking_${nombreSeccion}_${anio}.csv`;

        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const cambioTab = (v: any) => {
        setPage(0);
        setRowsPerPage(50);
        setFiltroRanking(v);

        // Forzar la selección de la primera área si pasamos a un ranking que la necesita
        if (v === 'Área QS' || v === 'Subárea QS') {
            if (areasDesdeAPI.length > 0) {
                setFiltroArea(JSON.stringify(areasDesdeAPI[0]));
            }
        } else {
            // Limpiamos el área si volvemos a 'Institucional'
            setFiltroArea(null);
        }

        // Limpiamos siempre la subárea para que no quede seleccionada por error
        setFiltroSubArea(null);
    };

    const seleccionArea = async (e: any) => {
        const areaSeleccionada: AreaActiva = JSON.parse(e.target.value);
        setFiltroArea(e.target.value);

        if (filtroRanking == 'Subárea QS') {
            const resultado = await ConsultasService.GetRankingSubAreasActivas(areaSeleccionada.iddecinvrankinginvestigadorarea);
            try {
                if (resultado && resultado.listData) {
                    setSubAreaDesdeAPI(resultado.listData);
                    const primeraSubarea = resultado.listData[0];
                    const subAreaString = JSON.stringify(primeraSubarea);
                    setFiltroSubArea(subAreaString);
                }
            } catch (error) {
                console.error("Error cargando configuración de subareas:", error);
            }
        }
    }

    const seleccionSubArea = (e: any) => {
        setFiltroSubArea(e.target.value);
    }

    const seleccionCuartil = async (e: any) => {
        setCuartil(e.target.value);
        setFiltroSubArea(null); // Borramos la subárea actual para obligar la recarga
    }

    const regresar = () => {
        navigate("/");
    }

    const getPosicionVariant = () => {
        if (tabValue !== "investigadores") {
            return 'standard';
        }

        if (filtroTipoInv === 'Profesor' && filtroRanking) {
            let configuracionActual;

            if (filtroRanking === 'Institucional') {
                configuracionActual = areasConfig.find(a => a.nivel === 'Institucional');
            } else if (filtroArea && filtroRanking === 'Área QS') {
                const areaObjeto = JSON.parse(filtroArea);
                configuracionActual = areasConfig.find(
                    a => a.nivel === filtroRanking && a.nombre.trim() === areaObjeto.nombre.trim()
                );
                //return 'no-medal';
            }
            //else if (filtroArea && filtroSubArea && filtroRanking === 'Subárea QS') {
            //    const areaObjeto = JSON.parse(filtroSubArea);
            //    configuracionActual = subAreasDesdeAPI.find(
            //        a => a.nivel === filtroRanking && a.nombre.trim() === areaObjeto.nombre.trim()
            //    );
            //return 'no-medal';
            //} 
            else if (filtroArea && filtroSubArea && filtroRanking === 'Subárea QS') {

                const areaObjeto = JSON.parse(filtroSubArea);
                configuracionActual = subAreasDesdeAPI.find(
                    a => a.nombre.trim() === areaObjeto.nombre.trim()
                );
                //return 'no-medal';
            }
            if (configuracionActual) {
                if (configuracionActual.top === 1) {
                    return 'gold-only';
                }
                if (configuracionActual.top === 3) {
                    return 'standard';
                }
            }
        } else if (filtroTipoInv === 'Técnico de investigación') {
            return 'gold-only';
        }

        return 'no-medal';
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
        setTabValue(newValue);
        setRowsPerPage(50);
        setPage(0);
    };

    const COLUMN_TOOLTIPS: Record<string, Record<string, string>> = {
        unidades: { // COLABORACIÓN
            metricaFinal: "(IRN (L-1) Norm. max ) x (0,5) + (PC Colab. Int Norm) x (0,5)",
            fwci: "Field-Weighted Citation Impact: Impacto de citas normalizado por campo.",
            productividad: "940*(Producción científica/Horas de Investigación)",
            horas: 'Total de horas con carga de investigación',
            produccion: "Número de publicaciones en Scopus",
            produccionColab: "Número de publicaciones con Colaboración Internacional",
            colaboracion: "Número de publicaciones con Colaboración Internacional respecto al total de publicaciones",
            l: "Total de países o territorios (según información de SciVal)",
            l1: "Total de países o territorios reducido en uno",
            p: "Total de socios/autores internacionales (según información de SciVal)",
            irn: "Índice de la Red de Investigación Internacional."
        },
        centros: { // UNIDADES
            metricaFinal: "(IRN (L-1) Norm. max ) x (0,5) + (PC Colab. Int Norm) x (0,5)",
            fwci: "Field-Weighted Citation Impact: Impacto de citas normalizado por campo.",
            productividad: "940*(Producción científica/Horas de Investigación)",
            horas: 'Total de horas con carga de investigación',
            produccion: "Número de publicaciones en Scopus",
            produccionColab: "Número de publicaciones con Colaboración Internacional",
            colaboracion: "Número de publicaciones con Colaboración Internacional respecto al total de publicaciones",
            l: "Total de países o territorios (según información de SciVal)",
            l1: "Total de países o territorios reducido en uno",
            p: "Total de socios/autores internacionales (según información de SciVal)",
            irn: "Índice de la Red de Investigación Internacional."
        },
        colaboracion: { // CENTROS
            metricaFinal: "(IRN (L-1) Norm. max ) x (0,5) + (PC Colab. Int Norm) x (0,5)",
            fwci: "Field-Weighted Citation Impact: Impacto de citas normalizado por campo.",
            productividad: "940*(Producción científica/Horas de Investigación)",
            horas: 'Total de horas con carga de investigación',
            produccion: "Número de publicaciones en Scopus",
            produccionColab: "Número de publicaciones con Colaboración Internacional",
            colaboracion: "Número de publicaciones con Colaboración Internacional respecto al total de publicaciones",
            l: "Total de países o territorios (según información de SciVal)",
            l1: "Total de países o territorios reducido en uno",
            p: "Total de socios/autores internacionales (según información de SciVal)",
            irn: "Índice de la Red de Investigación Internacional."
        },

    };

    return (
        <>
            <Box sx={{ p: { xs: 2, md: 4 } }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                    <Typography variant="h4" fontWeight="bold" sx={{ color: '#243165' }}>
                        Ranking {anio}
                    </Typography>
                    <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={regresar} >
                        Regresar
                    </Button>
                </Stack>
                <InfoBlock />
                <FormControl component="fieldset" sx={{ width: '100%', display: 'flex', alignItems: 'center' }}>
                    <RadioGroup
                        row
                        aria-label="tipo-reporte"
                        name="tipo-reporte"
                        value={tipo}
                        onChange={(e) => {
                            setTipo(e.target.value);
                        }}
                    >
                        <FormControlLabel
                            value="investigadores"
                            control={<Radio sx={{ color: '#243165', '&.Mui-checked': { color: '#243165' } }} />}
                            label="Investigadores"
                            sx={{ mr: 4 }}
                        />
                        <FormControlLabel
                            value="tecdocente"
                            control={<Radio sx={{ color: '#243165', '&.Mui-checked': { color: '#243165' } }} />}
                            label="Técnicos de investigación"
                            sx={{ mr: 4 }}
                        />
                        <FormControlLabel
                            value="centros"
                            control={<Radio sx={{ color: '#243165', '&.Mui-checked': { color: '#243165' } }} />}
                            label="Centros/Unidades"
                        />
                    </RadioGroup>
                </FormControl>
                <Paper elevation={3} sx={{ borderRadius: '16px', width: '100%', overflow: 'hidden' }}>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: '#f8f9fa' }}>
                        <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable" scrollButtons="auto" textColor="primary" indicatorColor="primary">

                            {tipo === "investigadores" && (
                                <Tab icon={<Person />} value="investigadores" iconPosition="start" label="investigadores" />
                            )}
                            {tipo === "tecdocente" && (
                                <Tab icon={<Person />} value="tecdocente" iconPosition="start" label="Técnicos Docentes" />
                            )}
                            {(cargo === "Profesor" && tipo === "investigadores") && (
                                <Tab icon={<Public />} value="colaboracion" iconPosition="start" label="Colaboración Int." />
                            )}

                            {(tipo === "centros") && (
                                <Tab icon={<School />} value="unidades" iconPosition="start" label="Unidades (Métricas)" />
                            )}
                            {(tipo === "centros") && (
                                <Tab icon={<BarChart />} value="centros" iconPosition="start" label="Centros (Métricas)" />
                            )}

                        </Tabs>
                    </Box>
                    <Box sx={{ p: { xs: 2, md: 3 } }}>
                        <Stack direction={{ xs: 'column', lg: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', lg: 'center' }} spacing={2} sx={{ mb: 3 }}>
                            <Box>
                                {tabValue === "investigadores" && (
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" gap={1}>
                                        <ToggleButtonGroup value={filtroTipoInv} exclusive onChange={(e, v) => v && setFiltroTipoInv(v)} color="primary" size="small" disabled={true}>
                                            {cargo == "Profesor" && (
                                                <ToggleButton value="Profesor">Profesor</ToggleButton>
                                            )}
                                            {cargo == "Técnico de investigación" && (
                                                <ToggleButton value="Técnico de investigación">Técnico</ToggleButton>
                                            )}
                                        </ToggleButtonGroup>
                                        {filtroTipoInv === 'Profesor' && (
                                            <ToggleButtonGroup value={filtroRanking} exclusive
                                                onChange={(e, v) => v && cambioTab(v)}
                                                color="primary" size="small">
                                                {tiposRankingUnicos.map(t => (<ToggleButton key={t} value={t}>{t}</ToggleButton>))}
                                            </ToggleButtonGroup>
                                        )}
                                        {mostrarColumnaArea && (
                                            <FormControl size="small" sx={{ minWidth: 200 }}>
                                                <InputLabel>Área</InputLabel>
                                                <Select
                                                    value={filtroArea || ''}
                                                    label="Área"
                                                    onChange={(e) => seleccionArea(e)}
                                                >
                                                    {areasDesdeAPI.map(a => (
                                                        <MenuItem
                                                            key={a.nombre}
                                                            value={JSON.stringify(a)}
                                                        >
                                                            {a.nombre}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        )}
                                        {mostrarColumnaSubarea && (
                                            <FormControl size="small" sx={{ minWidth: 200 }}>
                                                <InputLabel>Subárea</InputLabel>
                                                <Select value={filtroSubArea || ''} label="Subárea" onChange={(e) => seleccionSubArea(e)}>
                                                    {subAreasDesdeAPI.map(a => <MenuItem key={a.iddecinvrankinginvestigadorarea} value={JSON.stringify(a)}>{a.nombre}</MenuItem>)}
                                                </Select>
                                            </FormControl>
                                        )}

                                        {mostrarColumnaCuartil && (
                                            <>
                                                <FormControl size="small" sx={{ minWidth: 200 }}>
                                                    <InputLabel>Cuartil</InputLabel>
                                                    <Select value={cuartil || ''} label="cuartil" onChange={(e) => seleccionCuartil(e)}>
                                                        <MenuItem key={"Q1"} value={"Q1"}>{"Q1"}</MenuItem>
                                                        <MenuItem key={"Q2"} value={"Q2"}>{"Q2"}</MenuItem>
                                                        <MenuItem key={"Q3"} value={"Q3"}>{"Q3"}</MenuItem>
                                                        <MenuItem key={"Q4"} value={"Q4"}>{"Q4"}</MenuItem>
                                                    </Select>
                                                </FormControl>

                                                <FormControl size="small" sx={{ minWidth: 200 }}>
                                                    <InputLabel>Subárea</InputLabel>
                                                    <Select value={filtroSubArea || ''} label="Subárea" onChange={(e) => seleccionSubArea(e)}>
                                                        {subAreasDesdeAPI.map(a => <MenuItem key={a.nombre} value={JSON.stringify(a)}>{a.nombre}</MenuItem>)}
                                                    </Select>
                                                </FormControl>
                                            </>
                                        )}
                                    </Stack>
                                )}
                            </Box>

                            {(tabValue === "colaboracion" || tabValue === "unidades" || tabValue === "centros") && (
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    align="right"

                                >
                                    <InfoOutlined sx={{ fontSize: 16, verticalAlign: 'text-bottom', mr: 0.5 }} />
                                    Solo se consideran los socios con los que se tienen 3 o más publicaciones indexadas en Scopus durante los 5 años anteriores al año de premiación.
                                </Typography>
                            )}

                            <Button variant="contained" color="success" onClick={handleDownloadExcel} startIcon={<DownloadIcon />} disabled={currentData.length === 0} sx={{ backgroundColor: '#243165', color: '#fff !important', whiteSpace: 'nowrap' }}>
                                Descargar Tabla
                            </Button>
                        </Stack>
                        {(tabValue === "centros" || tabValue === "unidades") && (
                            <FormControl size="small" sx={{ minWidth: 220 }}>
                                <InputLabel id="orden-select-label">Ordenar por</InputLabel>
                                <Select
                                    labelId="orden-select-label"
                                    value={criterioOrden}
                                    label="Ordenar por"
                                    onChange={(e) => {
                                        setCriterioOrden(e.target.value);
                                        setPage(0); // Reiniciar paginación al cambiar el orden
                                    }}
                                >
                                    <MenuItem value="calificacion">Colaboración internacional</MenuItem>
                                    <MenuItem value="fwci">FWCI</MenuItem>
                                    <MenuItem value="productividad">Productividad científica</MenuItem>
                                </Select>
                            </FormControl>
                        )}
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell colSpan={columns.length} align="right" sx={{ borderBottom: 'none', py: 1 }}>
                                            <Stack alignItems="flex-end" spacing={0}>
                                                {(tabValue === "investigadores" || tabValue === "colaboracion") && (
                                                    <>
                                                        <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>Periodo de Publicación: {anio - 6}-{anio - 2}</Typography>
                                                        <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>Periodo de Citas: {anio - 5}-{anio - 1}</Typography>
                                                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                                                            <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>Fecha corte de Scival: {formatearFecha(fechaCreacion)}</Typography>
                                                            {isOficial && <Typography variant="caption" sx={{ color: 'primary.main', fontStyle: 'italic', fontWeight: 'bold' }}>- Corte Final</Typography>}
                                                        </Stack>
                                                        <Typography variant="caption" display="block" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>{autocitasLabel}</Typography>
                                                    </>
                                                )}
                                                {tabValue === "tecdocente" && (
                                                    <>
                                                        <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>Periodo de Publicación: {anio - 6}-{anio - 2}</Typography>
                                                        <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>Periodo de Citas: {anio - 5}-{anio - 1}</Typography>
                                                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                                                            <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>Fecha corte de Scival: {formatearFecha(fechaCreacion)}</Typography>
                                                            {isOficial && <Typography variant="caption" sx={{ color: 'primary.main', fontStyle: 'italic', fontWeight: 'bold' }}>- Corte Final</Typography>}
                                                        </Stack>
                                                        <Typography variant="caption" display="block" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>{autocitasLabel}</Typography>
                                                    </>
                                                )}
                                                {tabValue === "unidades" && (
                                                    <>
                                                        <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>Año de evaluación: {anio - 1}</Typography>
                                                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                                                            <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>Fecha corte de Scival: {formatearFecha(fechaCreacion)}</Typography>
                                                            {isOficial && <Typography variant="caption" sx={{ color: 'primary.main', fontStyle: 'italic', fontWeight: 'bold' }}>- Corte Final</Typography>}
                                                        </Stack>
                                                    </>
                                                )}
                                                {tabValue === "centros" && (
                                                    <>
                                                        <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>Año de evaluación: {anio - 1}</Typography>
                                                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                                                            <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>Fecha corte de Scival: {formatearFecha(fechaCreacion)}</Typography>
                                                            {isOficial && <Typography variant="caption" sx={{ color: 'primary.main', fontStyle: 'italic', fontWeight: 'bold' }}>- Corte Final</Typography>}
                                                        </Stack>
                                                    </>
                                                )}
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                    <TableRow sx={{ '& .MuiTableCell-head': { fontWeight: 'bold', bgcolor: 'grey.100' } }}>
                                        {columns.map((col) => {
                                            const tooltipText: string | undefined = COLUMN_TOOLTIPS[tabValue]?.[col.id];
                                            const isSorted = (tabValue === "centros" || tabValue === "unidades") && criterioOrden === col.id;

                                            return (
                                                <TableCell
                                                    key={col.id}
                                                    align={col.numeric ? 'right' : 'left'}
                                                    sx={{
                                                        // Resalte en la cabecera
                                                        backgroundColor: isSorted ? 'rgba(36, 49, 101, 0.1) !important' : 'transparent',
                                                        transition: 'background-color 0.3s'
                                                    }}
                                                >
                                                    <Stack
                                                        direction="row"
                                                        alignItems="center"
                                                        justifyContent={col.numeric ? 'flex-end' : 'flex-start'}
                                                        spacing={0.5}
                                                    >
                                                        <span>{col.label}</span>
                                                        {tooltipText && (
                                                            <Tooltip title={tooltipText} arrow placement="top">
                                                                <InfoOutlined sx={{ fontSize: 16, color: 'action.active', cursor: 'help', opacity: 0.6, '&:hover': { opacity: 1 } }} />
                                                            </Tooltip>
                                                        )}
                                                    </Stack>
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                </TableHead>

                                {/* --- TABLE BODY ACTUALIZADO CON ESTADO DE CARGA --- */}
                                <TableBody>
                                    {loadingDatos ? (
                                        <TableRow>
                                            <TableCell colSpan={columns.length} align="center" sx={{ py: 8 }}>
                                                <CircularProgress size={40} sx={{ color: '#243165' }} />
                                            </TableCell>
                                        </TableRow>
                                    ) : paginatedData.length > 0 ? (
                                        paginatedData.map((row, index) => (
                                            <TableRow key={index} hover>
                                                {columns.map((col) => {
                                                    const val = row[col.id as keyof typeof row];
                                                    const isSorted = (tabValue === "centros" || tabValue === "unidades") && criterioOrden === col.id;

                                                    return (
                                                        <TableCell
                                                            key={col.id}
                                                            align={col.numeric ? 'right' : 'left'}
                                                            sx={{
                                                                // Resalte en todo el cuerpo de la columna
                                                                backgroundColor: isSorted ? 'rgba(36, 49, 101, 0.03)' : 'transparent',
                                                                transition: 'background-color 0.3s'
                                                            }}
                                                        >
                                                            {col.id === 'posicion' ? (
                                                                <PosicionCell
                                                                    posicion={val as number}
                                                                    variant={getPosicionVariant()}
                                                                />
                                                            ) : (
                                                                (typeof val === 'number' && !Number.isInteger(val))
                                                                    ? val.toFixed(4)
                                                                    : val
                                                            )}
                                                        </TableCell>
                                                    );
                                                })}
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={columns.length} align="center" sx={{ py: 5 }}>
                                                <Typography variant="body1" color="text.secondary">
                                                    No hay datos disponibles para esta vista.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>

                            </Table>
                        </TableContainer>

                        <TablePagination rowsPerPageOptions={[10, 25, 50, 75, 100]} component="div" count={currentData.length} rowsPerPage={rowsPerPage} page={page} onPageChange={(e, p) => setPage(p)} onRowsPerPageChange={(e) => {
                            setRowsPerPage(Number(e.target.value));
                            setPage(0);
                        }} labelRowsPerPage="Filas:" />
                    </Box>
                </Paper>
            </Box>
        </>

    );
};

export default RankingVisualizador;